import { Injectable, computed, inject } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { defaultNavigation } from 'app/core/navigation/navigation.data';
import { environment } from '../../../environments/environment';
import { FuseNavigationItem } from '../../../@fuse/components/navigation';
import { PermissionsService } from '../services/permissions.service';
import { UserRole, NAVIGATION_ITEM_ACCESS } from '../constants/permissions.constants';

/**
 * Navigation Service
 * Manages dynamic navigation based on user role
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
    private permissionsService = inject(PermissionsService);

    /**
     * Computed navigation based on user role
     * Recalculates only when userRole changes
     */
    navigation = computed(() => this._computeNavigation());

    // ============================================================================
    // @ Private methods
    // ============================================================================

    /**
     * Compute navigation items based on user role and environment
     */
    private _computeNavigation(): Navigation {
        const userRole = this.permissionsService.getUserRole();

        if (!userRole) {
            return { default: [] };
        }

        let nav = this._filterNavigationByRole(defaultNavigation, userRole);

        // Filter test items in production
        if (environment.releaseStage === 'production') {
            nav = nav.filter((item) => item.id !== 'test');
        }

        return { default: nav };
    }

    /**
     * Recursively filter navigation items based on user role
     */
    private _filterNavigationByRole(
        items: FuseNavigationItem[],
        userRole: UserRole
    ): FuseNavigationItem[] {
        return items
            .map((item) => this._filterNavigationItem(item, userRole))
            .filter((item): item is FuseNavigationItem => item !== null);
    }

    /**
     * Filter a single navigation item and its children
     */
    private _filterNavigationItem(
        item: FuseNavigationItem,
        userRole: UserRole
    ): FuseNavigationItem | null {
        // Check if user has access to this item
        if (!this._canAccessItem(item, userRole)) {
            return null;
        }

        // If item has children, filter them recursively
        if (item.children && item.children.length > 0) {
            const filteredChildren = this._filterNavigationByRole(item.children, userRole);

            // If all children are hidden, hide the parent
            if (filteredChildren.length === 0) {
                return null;
            }

            return { ...item, children: filteredChildren };
        }

        return item;
    }

    /**
     * Check if user has access to a navigation item
     */
    private _canAccessItem(item: FuseNavigationItem, userRole: UserRole): boolean {
        // If item has no ID, allow access
        if (!item.id) {
            return true;
        }

        // Check if item is defined in NAVIGATION_ITEM_ACCESS
        const allowedRoles = NAVIGATION_ITEM_ACCESS[item.id];

        // If item is not in the access map, allow access (backward compatibility)
        if (!allowedRoles) {
            return true;
        }

        // Check if user's role is in allowed roles
        return allowedRoles.includes(userRole);
    }
}
